import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import type { EmbeddedWorkspaceSeed } from '../stores/workspace';
import { readDockMessage } from '../lib/dockMessages';
import type { DockPane } from '../lib/dockTabs';

export function useDockMessaging(options: {
  isEmbedded: boolean;
  detachedDock: Ref<DockPane | null>;
  dockFrame: Ref<HTMLIFrameElement | null>;
  getWorkspaceId: () => string | null;
  getSeed: () => EmbeddedWorkspaceSeed | null;
  getActiveEntityId: () => string | null;
  hasEntity: (id: string) => boolean;
  openEntity: (id: string) => Promise<void>;
}) {
  let embeddedReady = false;
  let pendingEntityId: string | null = null;
  let openGeneration = 0;
  let seedRequest: { resolve: (seed: EmbeddedWorkspaceSeed | null) => void; timer: ReturnType<typeof setTimeout> } | null = null;

  function finishSeedRequest(seed: EmbeddedWorkspaceSeed | null) {
    const request = seedRequest;
    if (!request) return;
    seedRequest = null;
    clearTimeout(request.timer);
    request.resolve(seed);
  }

  function requestDockWorkspaceSeed(workspaceId: string) {
    finishSeedRequest(null);
    return new Promise<EmbeddedWorkspaceSeed | null>((resolve) => {
      const timer = setTimeout(() => finishSeedRequest(null), 250);
      seedRequest = { resolve, timer };
      window.parent.postMessage({ type: 'chronis-dock-request-seed', workspaceId }, location.origin);
    });
  }

  async function openEmbeddedDockEntity(entityId: string) {
    if (!options.isEmbedded || options.getActiveEntityId() === entityId || !options.hasEntity(entityId)) return;
    const generation = ++openGeneration;
    await options.openEntity(entityId);
    const workspaceId = options.getWorkspaceId();
    if (generation !== openGeneration || options.getActiveEntityId() !== entityId || !workspaceId) return;
    history.replaceState({}, '', `/w/${workspaceId}/e/${entityId}?embed=1`);
  }

  async function markEmbeddedReady() {
    embeddedReady = true;
    if (!pendingEntityId) return;
    const entityId = pendingEntityId;
    pendingEntityId = null;
    await openEmbeddedDockEntity(entityId);
  }

  function syncDockFrame() {
    const dock = options.detachedDock.value;
    const entityId = dock?.tabs.find((tab) => tab.id === dock.activeTabId)?.entityId;
    const workspaceId = options.getWorkspaceId();
    if (!dock || !entityId || !workspaceId) return;
    options.dockFrame.value?.contentWindow?.postMessage({ type: 'chronis-dock-open-entity', workspaceId, entityId }, location.origin);
  }

  function receiveDockMessage(event: MessageEvent) {
    const fromFrame = readDockMessage(event, location.origin, options.dockFrame.value?.contentWindow ?? null);
    if (!options.isEmbedded && fromFrame?.type === 'chronis-dock-request-seed') {
      const seed = options.getSeed();
      if (seed && fromFrame.workspaceId === seed.workspace.id) options.dockFrame.value?.contentWindow?.postMessage({ type: 'chronis-dock-seed', workspaceId: seed.workspace.id, seed }, location.origin);
      return;
    }
    if (!options.isEmbedded) return;
    const fromParent = readDockMessage(event, location.origin, window.parent);
    if (fromParent?.type === 'chronis-dock-seed' && seedRequest && fromParent.workspaceId === location.pathname.match(/\/w\/([0-9a-f-]{36})/i)?.[1]) {
      finishSeedRequest(fromParent.seed);
      return;
    }
    if (fromParent?.type !== 'chronis-dock-open-entity') return;
    const { entityId, workspaceId } = fromParent;
    const routeWorkspaceId = location.pathname.match(/\/w\/([0-9a-f-]{36})/i)?.[1];
    if (workspaceId !== (options.getWorkspaceId() ?? routeWorkspaceId)) return;
    if (!embeddedReady) { pendingEntityId = entityId; return; }
    void openEmbeddedDockEntity(entityId);
  }

  watch(() => options.detachedDock.value?.activeTabId, () => { void nextTick(syncDockFrame); });
  onMounted(() => window.addEventListener('message', receiveDockMessage));
  onBeforeUnmount(() => {
    openGeneration += 1;
    finishSeedRequest(null);
    window.removeEventListener('message', receiveDockMessage);
  });
  return { requestDockWorkspaceSeed, markEmbeddedReady, syncDockFrame };
}
